import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Image, InputGroup, Modal, Spinner } from "react-bootstrap";
import { getAllImagesProducts } from "../../services/api";

export default function ProductImageGalleryModal({
  show,
  onHide,
  onSelect,
  selectedImageId,
  isConfirmingSelection = false,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [localSelectedId, setLocalSelectedId] = useState(null);

  const trimmedSearch = useMemo(() => searchTerm.trim(), [searchTerm]);

  useEffect(() => {
    if (show) {
      setSearchTerm("");
      setHasSearched(false);
      setImages([]);
      setError(null);
      setLocalSelectedId(selectedImageId ?? null);
    }
  }, [selectedImageId, show]);

  const fetchImages = useCallback(
    async (term) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getAllImagesProducts({
          search: term,
        });

        setImages(response?.data ?? []);
      } catch (err) {
        console.error("Erro ao buscar imagens da galeria", err);
        setError("Não foi possível carregar as imagens. Tente novamente.");
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!show) {
      return;
    }

    if (trimmedSearch === "") {
      setHasSearched(false);
      setImages([]);
      return;
    }

    const handle = setTimeout(() => {
      setHasSearched(true);
      fetchImages(trimmedSearch);
    }, 400);

    return () => clearTimeout(handle);
  }, [fetchImages, trimmedSearch, show]);

  useEffect(() => {
    if (isLoading || !hasSearched) {
      return;
    }

    if (localSelectedId && !images.some((item) => item.id === localSelectedId)) {
      setLocalSelectedId(null);
    }
  }, [hasSearched, images, isLoading, localSelectedId]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (trimmedSearch === "") {
      setHasSearched(false);
      setImages([]);
      return;
    }

    setHasSearched(true);
    fetchImages(trimmedSearch);
  };

  const handleSelect = (imageId) => {
    setLocalSelectedId(imageId);
  };

  const handleConfirmSelection = () => {
    if (!localSelectedId) {
      return;
    }

    const image = images.find((item) => item.id === localSelectedId);
    if (image) {
      onSelect?.(image);
    }
  };

  const renderContent = () => {
    if (!hasSearched && !isLoading) {
      return (
        <div className="text-muted text-center py-4">
          Utilize a busca para encontrar uma imagem existente.
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" role="status" />
        </div>
      );
    }

    if (error) {
      return <div className="text-danger text-center py-4">{error}</div>;
    }

    if (!images.length) {
      return (
        <div className="text-muted text-center py-4">
          Nenhuma imagem encontrada para a busca realizada.
        </div>
      );
    }

    return (
      <div
        className="d-flex align-items-stretch"
        style={{
          gap: "16px",
          overflowX: "auto",
          padding: "12px 4px",
        }}
      >
        {images.map((image) => {
          const isSelected = image.id === localSelectedId;
          const imageLabel = image.product_name || image.file_name || `Imagem ${image.id}`;

          return (
            <button
              type="button"
              key={image.id}
              className="border-0 bg-transparent p-0 flex-shrink-0"
              style={{ cursor: "pointer" }}
              onClick={() => handleSelect(image.id)}
              aria-pressed={isSelected}
              aria-label={`Selecionar imagem ${imageLabel}`}
            >
              <div
                className={`d-flex flex-column align-items-center rounded-3 ${
                  isSelected ? "border border-primary shadow" : "border border-light"
                }`}
                style={{
                  width: "150px",
                  padding: "12px",
                  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                  backgroundColor: "white",
                }}
              >
                <div
                  className="rounded-3 w-100 d-flex align-items-center justify-content-center"
                  style={{
                    height: "110px",
                    backgroundColor: "#f8f9fa",
                    overflow: "hidden",
                  }}
                >
                  <Image
                    src={image.thumbnail_url || image.url}
                    alt={imageLabel}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
                <small className="d-block mt-3 text-center text-secondary text-truncate w-100">
                  {imageLabel}
                </small>
                <div
                  className={`mt-3 rounded-circle border ${
                    isSelected ? "bg-primary border-primary" : "border-secondary"
                  }`}
                  style={{ width: "12px", height: "12px" }}
                  aria-hidden="true"
                />
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const shouldShowResultsSummary = hasSearched && trimmedSearch !== "";

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop={isConfirmingSelection ? "static" : true}
      keyboard={!isConfirmingSelection}
    >
      <Modal.Header closeButton={!isConfirmingSelection}>
        <Modal.Title>Resultados de busca</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="productImageSearch">
            <Form.Label className="visually-hidden">
              Buscar imagem do produto
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Buscar imagem por nome"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                autoFocus
              />
              <Button variant="primary" type="submit" disabled={isLoading}>
                Buscar
              </Button>
            </InputGroup>
          </Form.Group>
        </Form>

        {shouldShowResultsSummary && (
          <p className="mt-3 mb-2 text-muted">
            Resultados para: <strong>"{trimmedSearch}"</strong>
          </p>
        )}

        <div className="mt-2">{renderContent()}</div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isConfirmingSelection}>
          Fechar
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmSelection}
          disabled={!localSelectedId || isConfirmingSelection}
        >
          {isConfirmingSelection ? "Selecionando..." : "Selecionar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
